import { Dropdown as RBDropdown } from "react-bootstrap";
import { FloatingLabel } from "./FloatingLabel";
import PropTypes from 'prop-types';
import { HiddenValidationControl } from "./HiddenValidationControl";
import { makeLabel } from "../utils/generalUtils";
import { useEffect, useRef, useState } from "react";
import { withComponentCapabilities } from "./capabilities/componentCapabilities";
import Icon from "./Icon";

/**
 * A simple wrapper around a Bootstrap Dropdown.
 * 
 * We prefer the use of Select whenever possible for greater accessibility and platform support on
 * mobile devices. Dropdown is useful when the options to be rendered are not simple text, but rather
 * rich HTML entities. Dropdown supports these rich entities.
 * @component
 */
const Dropdown = withComponentCapabilities(({ id, variant, options, value: inputValue, labelField, valueField, selectOnly = false, hideLabel = false, placeholder, disabled = false, required = false, wildcard, className, onChange, href, style }) => {
  const [value, setValue] = useState(inputValue);
  const validationRef = useRef();

  useEffect(() => {
    setValue(inputValue);
  }, [inputValue]);

  const handleChange = newValue => {
    if (!selectOnly) {
      setValue(newValue);
    }
    if (!required || newValue) {
      onChange(newValue);
    } 
  }

  let displayLabel = wildcard || makeLabel(placeholder, required);
  if (value) {
    const opt = options?.find(opt => (valueField ? opt[valueField] : opt) == value);
    if (opt) {
      displayLabel = labelField ? opt[labelField] : opt;
    }
  }

  return <FloatingLabel hideLabel={hideLabel} placeholder={placeholder} required={required} value={value} href={href}>
        <RBDropdown onSelect={handleChange}>
            <RBDropdown.Toggle id={id} className={className ?? 'w-100'} variant={variant ?? 'dropdown'} disabled={disabled} style={{ style, paddingRight: '4px'}}>
              { (!variant || variant === 'dropdown') ? <div className="d-flex flex-row" style={{ alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                    {displayLabel}
                </div>
                 <div style={{ fontSize: '0.65em' }}>
                  <Icon variant="solid" kind="chevron-down"></Icon>
                </div>
              </div> : displayLabel }
            </RBDropdown.Toggle>
            <RBDropdown.Menu popperConfig={{strategy: 'fixed'}}>
                    {wildcard && <RBDropdown.Item key={null}>{wildcard}</RBDropdown.Item>}
                    {options?.map(opt => {
                        const value = valueField ? opt[valueField] : opt
                        const label = labelField ? opt[labelField] : opt
                        return <RBDropdown.Item key={value} eventKey={value}>{label}</RBDropdown.Item>
                    })}
            </RBDropdown.Menu>
        </RBDropdown>
        <HiddenValidationControl ref={validationRef} value={value} required={required}></HiddenValidationControl>
  </FloatingLabel>
}, {

});

Dropdown.propTypes = {
    /**
     * CSS class(es) to apply to the button of the dropdown to alter its appearance
     */
    className: PropTypes.string,

    /**
     * The visual variant of the button
     */
    variant: PropTypes.oneOf(['default', 'primary', 'success', 'danger', 'outline-light', 'outline-dark']),

    /**
     * The options to be rendered as choices by the dropdown
     */
    options: PropTypes.arrayOf(PropTypes.any),

    /**
     * The value to be displayed by the control
     */
    value: PropTypes.any,

    /**
     * Function to call when the value has been changed
     */
    onChange: PropTypes.func.isRequired,

    /**
     * The label for the control
     */
    placeholder: PropTypes.string.isRequired,

    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,

    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,

    /**
     * The field to read from the options as the label to display
     */
    labelField: PropTypes.string,

    /**
     * The field to read form the options as the value to be returned when the option is selected
     */
    valueField: PropTypes.string,

    /**
     * Set to true to stop the dropdown from showing its selected value
     */
    selectOnly: PropTypes.bool,

  /**
     * When provided indicates the dropdown should allow a null selection which is to be treated as a wildcard.
     * The string supplied is used as the label of the wildcard entry (standard use would be something like
     * '(any)' or '(all)' depending on the context)
     */
  wildcard: PropTypes.string,

  /**
   * An option navigation URL to be followed if the label text is clicked
   */
  href: PropTypes.string,
}

export default Dropdown;